import { Link, useMatchRoute } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { LockIcon, type LucideIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

export type NavigationItemProps = {
  className?: string;
  activeClassName?: string;
  isExpanded: boolean;
  isActive?: boolean;
  iconPlacement?: "prefix" | "suffix";
  icon?: LucideIcon;
  iconKey?: React.Key;
  isLocked?: boolean;
  onClickLocked?: () => void;
  label?: string;
  href?: string;
  onClick?: () => void;
  onHover?: () => void;
  children?: React.ReactNode;
  slots?: {
    icon?: React.ReactNode;
    label?: React.ReactNode;
  };
} & React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>;

export function NavigationItem({
  className,
  activeClassName,
  isExpanded,
  isActive,
  iconPlacement = "prefix",
  icon: Icon,
  iconKey,
  isLocked,
  onClickLocked,
  label,
  href,
  children,
  slots,
  ...baseProps
}: NavigationItemProps) {
  const matchRoute = useMatchRoute();

  if (isActive === undefined && href) {
    isActive = !!matchRoute({
      to: href,
      fuzzy: true,
    });
  }

  const props = {
    ...baseProps,
    className: twMerge(
      "group relative flex flex-col justify-stretch items-stretch h-13 px-4 text-theme-on-background outline-0 outline-blue-500 outline-offset-2 transition-[width,color,background-color,border-radius] duration-200 before:absolute before:inset-0 before:-z-1 before:content-[''] before:bg-linear-(--theme-gradient) before:rounded-[inherit] before:opacity-0 before:transition-[left,right,box-shadow,opacity] before:duration-200 focus-visible:outline-2",
      !isLocked &&
        "hover:text-white menu-glow cursor-pointer hover:before:opacity-100",
      isExpanded
        ? "w-full max-w-[300px] rounded-md before:inset-x-4 hover:before:inset-x-0"
        : "w-13 rounded-[26px]",
      className,
      isActive && ["text-theme-active! before:opacity-0!", activeClassName],
    ),
    "aria-current": isActive ? ("page" as const) : false,
  };

  children ??= (
    <div className="relative flex h-full w-full items-center justify-center">
      <div
        className={twMerge(
          "flex h-full w-full items-center justify-center",
          isLocked && "opacity-50",
        )}
      >
        {(Icon || slots?.icon) && iconPlacement === "prefix" && (
          <div className="grid size-5 shrink-0 grow-0 *:col-start-1 *:row-start-1">
            <AnimatePresence initial={false}>
              <motion.div
                key={iconKey}
                className="flex size-5 items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {slots?.icon ?? (Icon && <Icon className="size-5" />)}
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        <div className="flex w-0 min-w-0 grow text-left text-[11px] font-semibold">
          <AnimatePresence initial={false} mode="wait">
            {isExpanded && (
              <motion.div
                className={`w-full min-w-0 ${
                  iconPlacement === "prefix" ? "pl-5" : ""
                }`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.2,
                }}
              >
                <div className="truncate">{slots?.label ?? label}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {(Icon || slots?.icon) && iconPlacement === "suffix" && (
          <div className="grid size-5 shrink-0 grow-0 *:col-start-1 *:row-start-1">
            <AnimatePresence initial={false}>
              <motion.div
                key={iconKey}
                className="flex size-5 items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {slots?.icon ?? (Icon && <Icon className="size-5" />)}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      {isLocked && (
        <LockIcon className="size-3.5 shrink-0 opacity-50 transition-opacity duration-200 group-hover:opacity-100" />
      )}
    </div>
  );

  if (isLocked) {
    return (
      <button
        {...props}
        className={twMerge(props.className, "cursor-pointer")}
        {...(onClickLocked ? { onClick: onClickLocked } : { disabled: true })}
      >
        {children}
      </button>
    );
  }

  return href ? (
    <Link {...props} to={href}>
      {children}
    </Link>
  ) : (
    <button {...props}>{children}</button>
  );
}
